/**
 * F-FIRM-014 (Batch 16, 2026-05-20) — public, slug-keyed branding API.
 *
 * Wraps the anonymous endpoint `GET /v1/branding/by-slug/{slug}` registered
 * by `backend/src/handlers/branding-by-slug.ts`. Drives the white-label
 * rendering pipeline at `frontend/src/routes/__root.tsx` so a visitor on
 * a tenant subdomain (`iprotect.elreign.io`) sees the firm's logo, palette,
 * and firmName in the page chrome BEFORE sign-in.
 *
 * ## Why this is separate from `lib/api/branding.ts`
 *
 * `branding.ts` wraps the admin-side endpoint `GET /v1/admin/branding` which
 * REQUIRES a Supabase JWT + admin role and reads the tenant from the JWT.
 * This module wraps the unauthenticated, slug-keyed mirror — same data shape
 * (minus operational + PII fields) but a different fetch contract.
 *
 * ## Public shape vs admin shape
 *
 * The admin endpoint returns the full `TenantBrandingDoc` including
 * `customDomain`, `updatedAt`, `updatedByUserId`. This endpoint returns the
 * public-safe subset only — see `PublicTenantBranding` below. The backend
 * test `branding-by-slug.test.ts` asserts the operational fields are NEVER
 * exposed on the wire.
 *
 * ## Cache + retry policy
 *
 * `staleTime: 60_000` (60s) matches the `Cache-Control: max-age=60` header
 * the backend sets. TanStack Query won't re-fetch within the staleness
 * window — combined with the AFD edge cache, the net effect is that a
 * given (browser, slug) pair makes at most one network call per minute.
 *
 * `enabled: !!slug` — a `null` slug (apex elreign.io, app.elreign.io) skips
 * the query entirely. The root layout falls back to default Elreign branding
 * in that case.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from './client';

export interface PublicTenantBranding {
  firmName: string | null;
  tagline: string | null;
  logoUrl: string | null;
  primaryColor: string | null;
  accentColor: string | null;
  fontPair: string | null;
  tone: string | null;
  /** Firm's own support email — surfaced to the firm's clients on contact
   *  surfaces (e.g. account recovery) instead of an Elreign address. Optional
   *  on the type (older overlays / mocks omit it); the backend always sends it. */
  supportEmail?: string | null;
}

/**
 * Client Model B self-signup descriptor (2026-06-22). The backend now returns
 * this alongside `branding` on `GET /v1/branding/by-slug/{slug}`:
 *   - `enabled` — the firm has opted in to client self-signup. The branded
 *     signup page (`/auth/signup`) gates on this: enabled → render the page
 *     branded + drive the post-confirm `self-join`; disabled/404 → the normal
 *     global signup, unchanged.
 *   - `billingOwner` — WHO PAYS. `'firm'` → the individual onboarding wrapper
 *     skips the paid subscription step; `'client'` → the client pays as a
 *     normal b2c individual.
 *
 * A firm that opted IN but has no published branding now 200s (branding fields
 * all null + `openSignup.enabled: true`); a firm that did NOT opt in and has no
 * branding still 404s. So `openSignup` may be ABSENT on older/cached responses
 * — read it defensively (treat absent as `{ enabled: false, billingOwner:
 * 'client' }`).
 */
export interface PublicOpenSignup {
  enabled: boolean;
  billingOwner: 'firm' | 'client';
}

export interface PublicBrandingResponse {
  slug: string;
  /** May be all-null fields for an opted-in firm that hasn't published branding. */
  branding: PublicTenantBranding;
  /** Present on the live Model-B backend; absent on older/cached responses. */
  openSignup?: PublicOpenSignup;
  correlationId: string;
}

/**
 * Fetch a tenant's public branding overlay by subdomain slug.
 *
 * @param slug Tenant slug resolved from the current host via
 *             `resolveTenantSlugFromHost`. Pass `null` for apex/local-dev to
 *             skip the network call entirely.
 * @returns    TanStack Query result. `data` is the parsed response when the
 *             slug resolves to a tenant with published branding; `null` on
 *             404 (unknown slug OR no branding overlay yet — the wire-level
 *             404 is indistinguishable between the two cases by design).
 */
export function useTenantBrandingBySlug(slug: string | null) {
  return useQuery<PublicBrandingResponse | null>({
    queryKey: ['branding', 'public', slug],
    queryFn: async ({ signal }) => {
      if (!slug) return null;
      try {
        return await api<PublicBrandingResponse>(
          `/api/v1/branding/by-slug/${encodeURIComponent(slug)}`,
          { signal },
        );
      } catch (err) {
        // 404 is the canonical "no branding to apply" signal. Either the
        // slug is unknown OR the tenant exists but has never published a
        // branding overlay. Both fall through to "default Elreign branding"
        // in the consumer — bubble null, not the error.
        if (
          typeof err === 'object' &&
          err !== null &&
          'status' in err &&
          (err as { status: number }).status === 404
        ) {
          return null;
        }
        throw err;
      }
    },
    enabled: !!slug,
    // Matches the backend `Cache-Control: max-age=60`. Within this window
    // TanStack Query returns the cached value without refetching even on
    // remount, which prevents a flash-of-default-branding on route changes.
    staleTime: 60_000,
    // The branding rarely changes in a session; once cached it can live for
    // an hour before garbage collection.
    gcTime: 60 * 60_000,
    // Don't retry on 4xx — re-issuing a 404 won't change the answer.
    retry: (failureCount, err) => {
      if (
        typeof err === 'object' &&
        err !== null &&
        'status' in err &&
        typeof (err as { status: number }).status === 'number' &&
        (err as { status: number }).status >= 400 &&
        (err as { status: number }).status < 500
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
